import { action, computed, makeObservable, observable } from 'mobx';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { PlayerSectionName } from './playerFramesetSchema';
import { SectionStatus } from '../../pages/userPlayerPage/userPlayerPageState';

type Props = BindingProps<{
  widgetMode?: boolean | null,
  tutorialMode?: boolean | null,
  getSectionAvailability?: (section: PlayerSectionName) => boolean,
  getSectionStatus?: (section: PlayerSectionName) => SectionStatus
}>

const Frames: PlayerSectionName[] = [
  'Player',
  'Toolbar',
  'Transcripts',
  'Index',
  'Comments'
];

export class PlayerFramesetState
  extends StoreNode {

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable<this, 'widgetMode' | 'getSectionAvailablilty' | 'sectionStatus'>(this, {
      widgetMode: computed,
      getSectionAvailablilty: computed,
      sectionStatus: computed,
      tutorialMode: computed,
      isWidgetMode: computed,
      toggleSection: action,
      hideSection: action,
      showSection: action,
      setSectionVisibility: action,
      showSectionOnce: action,
      resetVisibleOnceSections: action,
      init: action,
      reset: action,
    });
  }

  private get widgetMode(): boolean | null {
    return this.getResolvedProp('widgetMode', null);
  }

  private get getSectionAvailablilty(): (section: PlayerSectionName) => boolean {
    return this.getResolvedProp('getSectionAvailability', null);
  }

  private get sectionStatus(): (section: PlayerSectionName) => SectionStatus {
    return this.getResolvedProp('getSectionStatus', null);
  }

  get tutorialMode(): boolean | null {
    return this.getResolvedProp('tutorialMode', null);
  }

  get isWidgetMode(): boolean {
    return this.store.widgetService.getIsWidgetMode(this.widgetMode);
  }

  readonly visibleFrames = observable.set<PlayerSectionName>(Frames);
  readonly visibleOnceSections = observable.set<PlayerSectionName>();

  toggleSection(name: PlayerSectionName) {
    const sections = this.visibleFrames;
    if (sections.has(name))
      sections.delete(name);
    else {
      if (!this.isSectionAvailable(name))
        return;
      sections.add(name);
    }
  }

  hideSection(name: PlayerSectionName) {
    return this.visibleFrames.delete(name);
  }
  showSection(name: PlayerSectionName) {

    if (!this.isSectionAvailable(name))
      return;

    return this.visibleFrames.add(name);
  }
  setSectionVisibility(name: PlayerSectionName, visibility: boolean) {
    if (!visibility)
      this.hideSection(name);
    else
      this.showSection(name);
  }

  showSectionOnce(name: PlayerSectionName) {
    if (this.visibleOnceSections.has(name))
      return;

    this.visibleOnceSections.add(name);
    return this.showSection(name);
  }

  resetVisibleOnceSections() {
    this.visibleOnceSections.clear();
  }

  isSectionVisible(name: PlayerSectionName) {
    return this.visibleFrames.has(name);
  }

  isSectionAvailable(name: PlayerSectionName) {
    if (this.getSectionAvailablilty)
      return this.getSectionAvailablilty(name);

    return true;
  }

  getSectionStatus(name: PlayerSectionName) {
    if (this.sectionStatus)
      return this.sectionStatus(name);

    return SectionStatus.NotEmpty;
  }

  init() {
    if (this.isWidgetMode) {
      this.visibleFrames.replace(Frames);
      this.hideSection('Transcripts');
      this.hideSection('Index');
      this.hideSection('Comments');
      this.hideSection('Toolbar');
    } else {
      this.visibleFrames.replace(Frames);
      Frames.forEach(item => {
        !this.isSectionAvailable(item) ?
          this.hideSection(item) :
          this.showSection(item);
      });
    }
  }

  reset() {
    this.resetVisibleOnceSections();
  }
}